import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageCircle, MessageSquare, Heart, CornerDownRight, CheckCheck, BadgeCheck, FileX, Banknote } from 'lucide-react';
import { apiGet, apiPost } from '../utils/api';

const POLL_INTERVAL_MS = 60000;

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'たった今';
    if (min < 60) return `${min}分前`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}時間前`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}日前`;
    return new Date(iso).toLocaleDateString('ja-JP');
  } catch {
    return '';
  }
}

function iconForType(type) {
  if (type === 'comment_on_your_post') return <MessageSquare className="w-4 h-4 text-rose-500 shrink-0" />;
  if (type === 'like_on_comment') return <Heart className="w-4 h-4 text-rose-400 shrink-0" />;
  if (type === 'reply_to_comment') return <CornerDownRight className="w-4 h-4 text-rose-400 shrink-0" />;
  if (type === 'evidence_approved') return <BadgeCheck className="w-4 h-4 text-emerald-500 shrink-0" />;
  if (type === 'evidence_rejected') return <FileX className="w-4 h-4 text-gray-400 shrink-0" />;
  if (type && type.startsWith('payout_')) return <Banknote className="w-4 h-4 text-emerald-500 shrink-0" />;
  return <MessageCircle className="w-4 h-4 text-rose-400 shrink-0" />;
}

function linkForNotification(n) {
  if (n.type === 'evidence_approved' || n.type === 'evidence_rejected') return '/my-evidence';
  if (n.type && n.type.startsWith('payout_')) return '/mypage';
  if (n.review_id) return `/post/${n.review_id}`;
  return null;
}

function NotificationBell({ user }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const buttonRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiGet('/api/notifications?limit=20');
      setItems(data?.notifications || []);
      setUnread(data?.unread_count || 0);
    } catch {
      // 未ログイン化・一時的な失敗は静かに無視（次のポーリングで回復）
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setUnread(0);
      return undefined;
    }
    fetchNotifications();
    const timer = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [user, fetchNotifications]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchNotifications();
  };

  const handleClickItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      setUnread((u) => Math.max(0, u - 1));
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      try {
        await apiPost('/api/notifications/read', { ids: [n.id] });
      } catch {
        // 既読化失敗は次回取得で整合
      }
    }
    const link = linkForNotification(n);
    if (link) {
      navigate(link);
    }
  };

  const handleMarkAllRead = async () => {
    setLoading(true);
    try {
      await apiPost('/api/notifications/read', {});
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch {
      // noop
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        className="relative text-gray-600 hover:text-rose-500 hover:bg-rose-50 p-2 rounded-full transition-colors"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={unread > 0 ? `通知 ${unread}件の未読` : '通知'}
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            aria-label="通知一覧"
            className="absolute right-0 mt-2 w-[min(20rem,calc(100vw-1.5rem))] bg-white rounded-2xl shadow-lg shadow-rose-100/60 border border-rose-100 z-20 overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-rose-100">
              <span className="text-sm font-semibold text-gray-700">通知</span>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  disabled={loading}
                  className="inline-flex items-center gap-1 text-xs text-rose-500 hover:text-rose-600 disabled:opacity-50"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  すべて既読
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-4 py-8 text-sm text-gray-400 text-center">
                  通知はまだありません。
                  <br />
                  気になる投稿にコメントすると、返信やいいねが届いたときにここでお知らせします。
                </p>
              ) : (
                items.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    role="menuitem"
                    onClick={() => handleClickItem(n)}
                    className={`w-full flex items-start gap-2.5 px-4 py-3 text-left border-b border-rose-50 last:border-b-0 transition-colors hover:bg-rose-50 ${
                      n.read ? 'bg-white' : 'bg-rose-50/60'
                    }`}
                  >
                    <span className="mt-0.5">{iconForType(n.type)}</span>
                    <span className="min-w-0">
                      <span className={`block text-xs leading-relaxed ${n.read ? 'text-gray-500' : 'text-gray-800 font-medium'}`}>
                        {n.message}
                      </span>
                      <span className="block text-[10px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</span>
                    </span>
                    {!n.read && (
                      <span className="ml-auto mt-1.5 w-2 h-2 rounded-full bg-rose-500 shrink-0" aria-hidden="true" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default NotificationBell;
